<script setup lang="ts">
import { useTemplateRef } from "vue";

import IconButton from "@/components/ui/IconButton/IconButton.vue";
import { useNativeEvents } from "@/composables/useNativeEvents";
import { eventTarget } from "@/utils/dom/dom";

interface Props {
  zoomOutDisabled?: boolean;
  zoomInDisabled?: boolean;
  zoomOutLabel: string;
  zoomInLabel: string;
}

defineProps<Props>();

const emit = defineEmits<{
  zoomOutStart: [];
  zoomInStart: [];
  zoomEnd: [];
  close: [];
}>();

const zoomInRef = useTemplateRef<InstanceType<typeof IconButton>>("zoomIn");
const rootRef = useTemplateRef("root");

const { addEventListener } = useNativeEvents();
addEventListener(document, "pointerdown", (event) => {
  const target = eventTarget(event);
  if (!rootRef.value?.parentElement?.contains(target)) {
    emit("close");
  }
});

defineExpose({
  focusZoomIn: () => void zoomInRef.value?.focus(),
});
</script>

<template>
  <div ref="root" class="zoom-popout">
    <IconButton
      icon="system/minus"
      size="small"
      :aria-label="zoomOutLabel"
      :disabled="zoomOutDisabled"
      @pointerdown.stop="emit('zoomOutStart')"
      @keydown.enter.space.stop="emit('zoomOutStart')"
      @pointerout="emit('zoomEnd')"
      @pointerup="emit('zoomEnd')"
      @keyup.stop="emit('zoomEnd')"
    />
    <IconButton
      ref="zoomIn"
      icon="system/plus"
      size="small"
      :aria-label="zoomInLabel"
      :disabled="zoomInDisabled"
      @pointerdown.stop="emit('zoomInStart')"
      @keydown.enter.space.stop="emit('zoomInStart')"
      @pointerout="emit('zoomEnd')"
      @pointerup="emit('zoomEnd')"
      @keyup.stop="emit('zoomEnd')"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/variables";
@use "@/styles/variables/colors";
@use "@/styles/mixins/shadow";

.zoom-popout {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  padding: 4px;
  background-color: colors.$background-ui;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: variables.$common-border-radius;
  gap: 2px;

  @include shadow.default;
}
</style>
